import { ModalButton } from '@/components/common/modal-button';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import type { PoiDetailProps } from '@/components/feature/shop/shop-poi-modal/poi-detail-horizontal';
import shopStyles from '@/components/feature/shop/shop-poi-modal/styles.module.scss';
import { useConfigStore } from '@/stores';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { buildFloorGuidePoiDetailTabOrders } from './buildTabOrders';
import detailStyles from './poi-detail-horizontal.module.scss';
import { DirectionIcon } from '@/components/icons';

type Props = {
    poiDetail: PoiDetailProps;
    language: string;
    onGuideStart: () => void;
    carouselCount?: number;
};

export const FloorGuidePoiDetailHorizontal = ({ poiDetail, language, onGuideStart, carouselCount = 0 }: Props) => {
    const { t } = useTranslation();
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const buildings = useConfigStore((s) => s.config?.server?.store?.buildings);
    const showBuildingName = Array.isArray(buildings) && buildings.length > 1;
    const tabOrders = buildFloorGuidePoiDetailTabOrders(poiDetail, { carouselCount });
    const r = tabOrders.regionOrder;

    return (
        <div className={clsx(detailStyles['floor-guide-poi-detail-horizontal'], shopStyles['poi-modal__layout'], shopStyles['poi-modal__layout--horizontal'])}>
            <div className={clsx(shopStyles['poi-modal__media'], shopStyles['poi-modal__media--horizontal'], 'focus-border')}>
                {poiDetail.imgUrlDetail ? (
                    <img src={poiDetail.imgUrlDetail} alt={poiDetail.poiName} />
                ) : (
                    <div className={shopStyles['poi-modal__media-empty']}>
                        <PlaceHolder variant="detail" />
                    </div>
                )}
            </div>

            <div
                className={clsx(
                    shopStyles['poi-modal__panel'],
                    shopStyles['poi-modal__panel--horizontal'],
                    detailStyles['floor-guide-poi-detail-horizontal__panel']
                )}
            >
                <div className={clsx(shopStyles['poi-modal__container'], detailStyles['floor-guide-poi-detail-horizontal__container'])}>
                    <h1
                        data-interactive
                        data-modal-region
                        tabIndex={tabOrders.name}
                        data-modal-region-order={r.header}
                        aria-label={poiDetail.poiName}
                        className={clsx(shopStyles['poi-modal__title'], 'focus-border')}
                    >
                        {poiDetail.poiName}
                    </h1>
                    <p
                        className={clsx(shopStyles['poi-modal__meta'], 'focus-border')}
                        data-interactive
                        tabIndex={tabOrders.meta}
                        aria-label={showBuildingName ? `${poiDetail.floorName} ${poiDetail.buildingName}` : poiDetail.floorName}
                    >
                        <span>{poiDetail.floorName}</span>
                        {showBuildingName && <> ·<span>{poiDetail.buildingName}</span></>}
                    </p>

                    <div
                        className={clsx(
                            shopStyles['poi-modal__content'],
                            detailStyles['floor-guide-poi-detail-horizontal__content'],
                            'scrollbar-custom-gray-99938f scrollbar-w-6'
                        )}
                    >
                        <div className={shopStyles['poi-modal__rows']}>
                            {poiDetail.listContent?.map((content, index) => (
                                <div
                                    className={clsx(shopStyles['poi-modal__row'], 'focus-border')}
                                    key={content.key}
                                    data-interactive
                                    {...(index === 0 ? { 'data-modal-region': true, 'data-modal-region-order': r.shopInfo } : {})}
                                    tabIndex={tabOrders.shopInfo[index]}
                                    aria-label={`${content.name?.[language]} ${content.content?.[language]}`}
                                >
                                    <div className={shopStyles['poi-modal__row-key']}>{content.name?.[language]?.slice(0, 12)}</div>
                                    <div className={shopStyles['poi-modal__row-name']}>{content.content?.[language]}</div>
                                </div>
                            ))}
                        </div>

                        {poiDetail.desc && (
                            <div
                                className={clsx(shopStyles['poi-modal__desc'], 'focus-border')}
                                data-interactive
                                tabIndex={tabOrders.desc}
                                aria-label={poiDetail.desc}
                            >
                                {poiDetail.desc}
                            </div>
                        )}

                        {poiDetail.keywords.length > 0 && (
                            <div className={shopStyles['poi-modal__tags']}>
                                {poiDetail.keywords.map((keyword, index) => (
                                    <span
                                        key={keyword}
                                        className={clsx(shopStyles['poi-modal__tag'], 'focus-border', {
                                            [shopStyles['poi-modal__tag--contrast']]: contrast,
                                        })}
                                        data-interactive
                                        tabIndex={tabOrders.keywords[index]}
                                        aria-label={keyword}
                                    >
                                        {keyword}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className={clsx(shopStyles['poi-modal__actions'], shopStyles['poi-modal__actions--single'])}>
                    <ModalButton
                        size="fit"
                        variant="primary"
                        className={shopStyles['poi-modal__action-button']}
                        type="button"
                        role="button"
                        tabIndex={tabOrders.guide}
                        data-modal-region
                        data-modal-region-order={r.guide}
                        data-interactive
                        aria-label={t('shop.modal.text.buttons.direction_guide')}
                        icon={<DirectionIcon width={28} height={28} />}
                        onClick={onGuideStart}
                        data-role={t('common.data_role.button')}
                    >
                        {t('shop.modal.text.buttons.direction_guide')}
                    </ModalButton>
                </div>
            </div>
        </div>
    );
};
